// libs
import { useApolloClient } from 'hooks/useApolloClient'
// types
import {
  IDashboard,
  IDashboardItem,
  IMutationResponse,
  IDashboardCreateInput,
  IDashboardUpdateInput,
  IDashboardDeleteInput,
  IGetDashboardsResponse,
  IDashboardItemCreateInput,
  IDashboardItemUpdateInput,
  IGetDashboardItemsResponse,
  IDashboardUpdateMutationArgs,
  IDashboardCreateMutationArgs,
  IDashboardDeleteMutationArgs,
  IDashboardItemUpdateMutationArgs,
  IDashboardItemCreateMutationArgs,
  IDashboardItemDeleteMutationArgs
} from 'types/graphql'
// graphql
import {
  DASHBOARD_CREATE_MUTATION,
  DASHBOARD_DELETE_MUTATION,
  DASHBOARD_ITEM_CREATE_MUTATION,
  DASHBOARD_ITEM_DELETE_MUTATION,
  DASHBOARD_ITEM_UPDATE_MUTATION,
  DASHBOARD_UPDATE_MUTATION
} from 'graphql/dashboards/mutations'
import { DASHBOARDS_ITEMS_QUERY, DASHBOARDS_QUERY } from 'graphql/dashboards/queries'
// utils
import { handleError } from 'utils/errors/handlers'

export const useDashboardsAPI = () => {
  const { client } = useApolloClient()

  const getDashboards = async () => {
    try {
      const response = await client.query<{ dashboards: IGetDashboardsResponse }>({
        query: DASHBOARDS_QUERY
      })

      return response.data.dashboards.data
    } catch (e) {
      handleError(e, 'useDashboardsAPI.getDashboards')
    }
  }

  const getDashboardItems = async (id: string) => {
    try {
      const response = await client.query<{ dashboardItems: IGetDashboardItemsResponse }>({
        query: DASHBOARDS_ITEMS_QUERY,
        variables: {
          input: {
            dashboardId: id
          }
        }
      })

      return response.data.dashboardItems.data
    } catch (e) {
      handleError(e, 'useDashboardsAPI.getDashboardItems')
    }
  }

  const createDashboard = async (input: IDashboardCreateInput) => {
    try {
      const response = await client.mutate<Record<'dashboardCreate', IDashboard>, IDashboardCreateMutationArgs>({
        mutation: DASHBOARD_CREATE_MUTATION,
        variables: {
          input
        }
      })

      return response.data.dashboardCreate
    } catch (e) {
      handleError(e, 'useDashboardsAPI.createDashboard')
    }
  }

  const updateDashboard = async (input: IDashboardUpdateInput) => {
    try {
      return await client.mutate<IDashboard, IDashboardUpdateMutationArgs>({
        mutation: DASHBOARD_UPDATE_MUTATION,
        variables: {
          input
        }
      })
    } catch (e) {
      handleError(e, 'useDashboardsAPI.updateDashboard')
    }
  }

  const deleteDashboard = async (input: IDashboardDeleteInput) => {
    await client.mutate<IDashboard, IDashboardDeleteMutationArgs>({
      mutation: DASHBOARD_DELETE_MUTATION,
      variables: {
        input
      }
    })
  }

  const createDashboardItem = async (input: IDashboardItemCreateInput) => {
    try {
      const response = await client.mutate<
        Record<'dashboardItemCreate', IDashboardItem>,
        IDashboardItemCreateMutationArgs
      >({
        mutation: DASHBOARD_ITEM_CREATE_MUTATION,
        variables: {
          input
        }
      })

      return response.data.dashboardItemCreate
    } catch (e) {
      handleError(e, 'useDashboardsAPI.createDashboard')
    }
  }

  const updateDashboardItem = async (input: IDashboardItemUpdateInput) => {
    await client.mutate<IDashboardItem, IDashboardItemUpdateMutationArgs>({
      mutation: DASHBOARD_ITEM_UPDATE_MUTATION,
      variables: {
        input
      }
    })
  }

  const deleteDashboardItem = async (id: string) => {
    await client.mutate<IMutationResponse, IDashboardItemDeleteMutationArgs>({
      mutation: DASHBOARD_ITEM_DELETE_MUTATION,
      variables: {
        input: {
          id
        }
      }
    })
  }

  return {
    getDashboards,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    getDashboardItems,
    createDashboardItem,
    updateDashboardItem,
    deleteDashboardItem
  }
}
